import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { FormikSendRecipientPicker, SendDialogActions } from "../../shared/SendRecipientPicker";
import { sendDeviation } from "../../../../api/quality/deviation";
import { fetchDeviationNotificationSettings } from "../../../../api/quality/deviationNotificationSettings";
import { dateToString } from "../../../Utils";

const buildRecipientOptions = (deviation) => {
  const customer = deviation?.customer;
  const project = deviation?.project;
  const contact = project?.contact || deviation?.contact;
  const workOrder = deviation?.work_order;
  const projectManager = project?.project_manager;
  const internalProjectLeader = workOrder?.responsible_participant?.user;
  const projectLeader =
    projectManager ||
    workOrder?.responsible_executor_participant?.user ||
    internalProjectLeader;

  const options = [
    {
      key: "project_leader",
      label: "Prosjektleder",
      name: projectLeader?.displayTitle,
      email: projectLeader?.email,
    },
    {
      key: "internal_project_leader",
      label: "Intern prosjektleder",
      name: internalProjectLeader?.displayTitle,
      email: internalProjectLeader?.email,
    },
    {
      key: "contact",
      label: "Kontakt hos kunde",
      name: contact?.displayTitle || contact?.name,
      email: contact?.email,
    },
    {
      key: "customer",
      label: "Kunde",
      name: customer?.displayTitle || customer?.name,
      email: customer?.email,
    },
  ].filter((option) => option.email);

  const seen = new Set();

  return options.filter((option) => {
    const email = option.email?.toLowerCase();
    if (!email || seen.has(email)) return false;
    seen.add(email);
    return true;
  });
};

const buildProjectContactOptions = (deviation, recipientOptions) => {
  const seenEmails = new Set(
    (recipientOptions || []).map((option) => option.email?.toLowerCase()).filter(Boolean)
  );

  return (deviation?.project?.project_contacts || [])
    .filter((projectContact) => projectContact?.id && projectContact?.email)
    .filter((projectContact) => {
      const email = projectContact.email.toLowerCase();
      if (seenEmails.has(email)) return false;
      seenEmails.add(email);
      return true;
    })
    .map((projectContact) => ({
      id: projectContact.id,
      label: projectContact.contact_type || "Prosjektkontakt",
      name:
        projectContact.displayTitle ||
        projectContact.user?.displayTitle ||
        projectContact.contact?.displayTitle ||
        projectContact.customer_contact?.displayTitle,
      email: projectContact.email,
      mobile: projectContact.mobile,
      sourceType: projectContact.source_type,
    }));
};

export const DeviationSendDialog = ({
  open,
  onClose,
  deviation,
  onSent,
}) => {
  const { t } = useTranslation();
  const [notificationSettings, setNotificationSettings] = useState({
    send_mode: "immediate",
    copy_sender: true,
  });

  useEffect(() => {
    if (!open) return;
    fetchDeviationNotificationSettings()
      .then((data) => {
        setNotificationSettings({
          send_mode: data?.send_mode === "digest" ? "digest" : "immediate",
          copy_sender: data?.copy_sender !== false,
        });
      })
      .catch(() => {});
  }, [open]);

  const recipientOptions = useMemo(
    () => buildRecipientOptions(deviation),
    [deviation]
  );
  const projectContactOptions = useMemo(
    () => buildProjectContactOptions(deviation, recipientOptions),
    [deviation, recipientOptions]
  );
  const projectManagerOption = recipientOptions.find(
    (option) => option.key === "project_leader"
  );

  const pickerOptions = [...recipientOptions, ...projectContactOptions.map((option) => ({ ...option, key: `project_contact:${option.id}`, group: "customer" }))];
  const isExtra = (row) => !pickerOptions.some((option) => option.key === row.key);

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="Send informasjon om avvik"
      size="3xl"
    >
      <Formik
        enableReinitialize
        initialValues={{
          id: deviation?.id,
          recipients: [],
          recipient_types: [],
          project_contact_ids: [],
          custom_name: "",
          custom_email: "",
          message: "",
        }}
        validationSchema={Yup.object({
          custom_email: Yup.string().email("Ugyldig e-post"),
        })}
        onSubmit={async (values, helpers) => {
          try {
            const selected = values.recipients || [];
            const extra = selected.find(isExtra);
            const response = await sendDeviation({
              id: values.id, message: values.message,
              recipient_types: selected.filter((row) => recipientOptions.some((option) => option.key === row.key)).map((row) => row.key),
              project_contact_ids: selected.filter((row) => String(row.key).startsWith("project_contact:")).map((row) => row.id),
              custom_name: extra?.name || "", custom_email: extra?.email || "",
            });

            if (response?.needs_follow_up || response?.failed?.length) {
              toast.error(
                response?.errors?.[0] ||
                  response.failed?.[0]?.error ||
                  t("deviation.send.success_partial")
              );
            } else if (response?.send_mode === "digest") {
              toast.success(
                t("deviation.send.success_scheduled", {
                  when: response?.scheduled_at
                    ? dateToString(response.scheduled_at)
                    : "",
                })
              );
            } else {
              toast.success(t("deviation.send.success_sent"));
            }
            onSent?.(response?.deviation || deviation);
            helpers.setSubmitting(false);
            onClose?.();
          } catch (error) {
            console.error(error);
            const msg =
              (Array.isArray(error?.errors) && error.errors.filter(Boolean)[0]) ||
              error?.message ||
              t("deviation.send.failed");
            toast.error(msg);
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form className="space-y-6">
            <div className="space-y-4">
              <FormikSendRecipientPicker
                formik={formik} valueField="recipients" multiple autoFocus
                options={pickerOptions} customerId={deviation?.customer?.id || deviation?.project?.customer?.id}
                canSelect={(item, selected) => !isExtra(item) || !selected.some(isExtra)}
              />
              <p className="pm-help">{t("recipients.extraLimit")}</p>

              <div>
                <p className="text-sm text-gray-500">
                  Prosjektleder og kontakt hentes fra prosjektet dersom de er registrert der.
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {notificationSettings.send_mode === "digest"
                    ? t("deviation.send.digest_hint")
                    : t("deviation.send.immediate_hint")}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {notificationSettings.copy_sender
                    ? t("deviation.send.copy_always")
                    : t("deviation.send.copy_off")}
                  {projectManagerOption?.name
                    ? ` Prosjektleder på prosjektet er ${projectManagerOption.name}.`
                    : ""}
                </p>
              </div>
            </div>

            <PMField
              name="message"
              label="Melding"
              multiline
              rows={6}
              placeholder="Skriv en kort melding til mottakeren"
              {...formik}
            />

            <SendDialogActions
              onCancel={onClose}
              onSubmit={formik.submitForm}
              submitting={formik.isSubmitting}
              cancelLabel="Avbryt"
              sendLabel="Send"
              sendingLabel={t("Sending...")}
            />
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
